// legacy/state-bridge.js — Store ↔ state.js synchronization bridge
//
// Mirrors listing-draft fields between interaction/stores/listing-draft.store.js
// and the legacy state.js bus so that unmigrated Steps (4/5/6/7) that still
// call getState('listingDraft*') continue to work.
//
// Strategy: bidirectional sync.
//   - store → state: when store fires, mirror to state.js
//   - state → store: when state.js fires on a draft key, mirror back to store
//
// @legacy Will be removed in PR-15 when all Steps read from store directly.
// @module

import * as store from '../interaction/stores/listing-draft.store.js';

const DRAFT_KEYS = [
  'listingDraftTitle',
  'listingDraftBullets',
  'listingDraftDescription',
  'listingDraftSearchTerms',
  'listingDraftQa',
  'listingDraftV1',
  'optimizerResult',
];

let unsubStore = null;
let unsubState = [];

/**
 * Initialize the bridge. Call once at app startup (after state.js is ready).
 * @param {object} stateApi - { getState, setState, subscribe } from state.js
 */
export function initStateBridge({ getState, setState, subscribe }) {
  // Guard against double-init
  if (unsubStore) return;

  // Seed store with current state values (restore / persisted session)
  const seed = {};
  for (const key of DRAFT_KEYS) {
    const val = getState(key);
    if (val !== null && val !== undefined) {
      seed[key] = val;
    }
  }
  if (Object.keys(seed).length > 0) {
    store.replaceDraft(seed);
  }

  // ── store → state (primary direction) ──
  unsubStore = store.subscribe((_changedFields) => {
    const snap = store.getSnapshot();
    for (const key of DRAFT_KEYS) {
      const storeVal = snap[key];
      const stateVal = getState(key);
      if (storeVal !== stateVal) {
        setState(key, storeVal);
      }
    }
  });

  // ── state → store (backward compat for legacy setState callers) ──
  for (const key of DRAFT_KEYS) {
    const unsub = subscribe(`state:${key}Changed`, ({ curr }) => {
      const storeVal = store.getSnapshot()[key];
      if (curr !== storeVal) {
        store.patchDraft(key, curr);
      }
    });
    unsubState.push(unsub);
  }
}

/**
 * Teardown (for testing).
 */
export function destroyBridge() {
  if (unsubStore) { unsubStore(); unsubStore = null; }
  for (const fn of unsubState) { fn(); }
  unsubState = [];
}
